import React, { memo } from 'react';
import { Cell, DatePicker, Row, Select, Tooltip } from '@dexma/ui-components';
import { StyledSelectCell, StyledWidgetSelector } from 'adapter/in/common/components/Selector/style';

import gtmIds from 'adapter/in/dashboards/config/gtm';
import { ENTITY_TYPE } from 'adapter/in/common/config/entityTypes';
import { SelectorOption, SelectorValueOptions } from 'adapter/in/common/form/types';
import { FooterInputText } from 'adapter/in/common/components/Text';
import i18n from 'asset/translation';
import { normalizeDateFormat } from 'adapter/in/common/utils/DateUtils';
import dayjs, { Dayjs } from 'dayjs';
import { FormSelectorLogo } from 'adapter/in/common/components/Selector/FormSelectorLogo';
import { ENVIRONMENT } from 'domain/entity/environment';

type CommonProps = {
    label: string;
    name: string;
    placeholder?: string;
    isLoading: boolean;
    error: boolean;
    maxSelectionLength: number;
    minSelectionLength: number;
    onChange: (selectedValue: string | string[]) => void;
    onClear?: (onChangeCallback: (e: string | string[]) => void) => void;
    handleButtonSelectAll?: (values: string[]) => void;
    handleClearAll?: () => void;
    disabled?: boolean;
    errorMessage?: string;
    footer?: string;
    style?: React.CSSProperties;
    dateFormat?: string;
    displaySelectedTooltip?: boolean;
    maxTagCount?: number;
};

type DatePickerProps = CommonProps & {
    asDatePicker: true;
    options?: undefined;
    value?: string;
};

type SelectProps = CommonProps & {
    asDatePicker?: false;
    options: SelectorOption[] | undefined;
    value: SelectorValueOptions | undefined;
};

export type WidgetSelectorType = DatePickerProps | SelectProps;

export const mapValues = (values?: SelectorOption[]): string[] => (values ? values.map((value: SelectorOption) => value.value) : []);

const isLogoOptionValue = (optionValue: string | null | undefined): boolean => optionValue?.startsWith('image_icon/') ?? false;

const getLogoUrl = (optionValue: string | null | undefined, optionLogoUrl?: string): string | undefined => {
    if (optionLogoUrl) {
        return optionLogoUrl;
    }

    const baseUrl =
        process.env.ENV === ENVIRONMENT.DEVELOPMENT ? 'https://app.pre.dexma.com' : `${window.location.protocol}//${window.location.host}`;

    return isLogoOptionValue(optionValue) && optionValue && baseUrl ? `${baseUrl}/repository/${optionValue}` : undefined;
};

const renderSelectorLabel = (label: React.ReactNode, optionValue: string, isSelected?: boolean, optionLogoUrl?: string) => {
    const logoUrl = getLogoUrl(optionValue, optionLogoUrl);

    if (!logoUrl) {
        return <div style={{ display: 'flex', alignItems: 'center' }}>{label}</div>;
    }

    return <FormSelectorLogo logoName={label} logoUrl={logoUrl} isSelected={isSelected} />;
};

export const renderSelectorOption = (option: any) => {
    const optionValue = option.data?.value ?? '';
    const optionLogoUrl = option.data?.logoUrl;
    return renderSelectorLabel(option.label, optionValue, false, optionLogoUrl);
};

export const renderSelectedOption = (option: { label: React.ReactNode; value: string | number; logoUrl?: string }) =>
    renderSelectorLabel(option.label, String(option.value), true, option.logoUrl);

const FormSelector = memo((props: WidgetSelectorType) => {
    const {
        label,
        onChange,
        onClear,
        handleButtonSelectAll,
        handleClearAll,
        disabled,
        placeholder,
        isLoading,
        error,
        errorMessage,
        maxSelectionLength,
        minSelectionLength,
        name,
        footer,
        style,
        dateFormat,
        displaySelectedTooltip,
        maxTagCount,
    } = props;

    const parsedValues = !props.asDatePicker ? mapValues(props.value as SelectorOption[]) : undefined;

    const isMultiple = maxSelectionLength > 1;
    const allowClear = !disabled && (minSelectionLength === 0 || isMultiple);
    const shouldRenderSelectedLogo =
        !props.asDatePicker && (props.options ?? []).some((option) => isLogoOptionValue(option.value) || option.logoUrl);
    const effectiveClearHandler = handleClearAll ?? (onClear as (() => void) | undefined) ?? (() => onChange(isMultiple ? [] : ''));

    const selectedTooltip =
        !isMultiple && !props.asDatePicker && parsedValues?.length
            ? (props.options ?? []).find((opt) => opt.value === parsedValues[0])?.label
            : undefined;

    return (
        <StyledWidgetSelector $error={!isLoading && error} style={style}>
            <Row>
                <Cell className='widget-type-block'>
                    <span>{label}</span>
                </Cell>
            </Row>

            <Row>
                <StyledSelectCell data-entity-id={name} data-entity-type={ENTITY_TYPE.FORM_FIELD}>
                    {props.asDatePicker ? (
                        <DatePicker
                            style={{ width: '50%' }}
                            type='date'
                            picker='week'
                            label='Select week'
                            format={normalizeDateFormat(dateFormat)}
                            value={props.value ? dayjs(props.value) : null}
                            onChange={(dateAsDayjs: Dayjs | null) => {
                                onChange(dateAsDayjs ? dateAsDayjs.toISOString() : '');
                            }}
                            calendarIconAriaLabel={i18n.t('common.ariaLabel.datePicker.calendarIcon')}
                            prevPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.prevPageIcon')}
                            nextPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.nextPageIcon')}
                            superPrevPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.superPrevPageIcon')}
                            superNextPageIconAriaLabel={i18n.t('common.ariaLabel.datePicker.superNextPageIcon')}
                            disabled={disabled}
                        />
                    ) : (
                        <Tooltip title={displaySelectedTooltip ? selectedTooltip : undefined}>
                            <Select
                                className={`select_${name}`}
                                data-testid={name}
                                dataId={gtmIds.addWidget.sidePanel.form.select}
                                popupMatchSelectWidth={false}
                                isLoading={isLoading}
                                placeholder={placeholder}
                                options={props.options}
                                maxTagCount={maxTagCount}
                                optionRender={renderSelectorOption}
                                selectedOptionRender={shouldRenderSelectedLogo ? renderSelectedOption : undefined}
                                onChange={onChange}
                                onClear={onClear as any}
                                mode={isMultiple ? 'multiple' : 'single'}
                                handleButtonSelectAll={handleButtonSelectAll}
                                handleClearAll={effectiveClearHandler}
                                defaultValues={parsedValues}
                                value={parsedValues}
                                pageSize={isMultiple ? maxSelectionLength : undefined}
                                allowClear={allowClear}
                                showOptionsAriaLabel={i18n.t('common.ariaLabel.showSelectOptions')}
                                hideOptionsAriaLabel={i18n.t('common.ariaLabel.hideSelectOptions')}
                                clearAllOptionsAriaLabel={i18n.t('common.ariaLabel.clearAllOptions')}
                                deleteOptionSelectedAriaLabel={i18n.t('common.ariaLabel.deleteOptionSelected')}
                                disabled={disabled}
                            />
                        </Tooltip>
                    )}

                    {!isLoading && error && <div className='error'>{errorMessage}</div>}
                </StyledSelectCell>
            </Row>

            {footer && <FooterInputText text={footer} />}
        </StyledWidgetSelector>
    );
});

export default FormSelector;
